import { CancelToken } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Container } from '@hivebrite/design-system/Container';
import { primaryShadow } from '@hivebrite/design-system/Theme';

import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import SuperCategoryCard from 'hb-react/shared/components/DesignSystem/SuperCategoryCard';
import { CANCEL_FETCH_MESSAGE } from 'hb-react/shared/constants/cancelFetch';
import useDisplayNavigationDropShadow from 'hb-react/shared/hooks/theme/useDisplayNavigationDropShadow';
import useSecondaryNavigationTheme from 'hb-react/shared/hooks/theme/useSecondaryNavigationTheme';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import { HTTP_STATUS_CODE_403 } from '../constants/httpStatusCode';
import { SUPER_CATEGORIES } from '../constants/types';
import { useTopicsIndexContext } from '../contexts/TopicsIndexContext';
import useTopicsMenuName from '../hooks/useTopicsMenuName';
import fetchSuperCategories from '../requests/fetchSuperCategories';

const CustomCol = ({ children }) => (
  <Col width={{ _: 1, m: 1 / 2, l: 1 / 3 }} pb={{ _: 3, m: 7, l: 6 }}>
    {children}
  </Col>
);

const getSeeGroupsLabels = (totalGroups, menuName, groupTitle) => {
  if (totalGroups > 0) {
    return [
      s__('Topics|See %{totalGroups} %{menuName}').i__({
        totalGroups,
        menuName,
      }),
      s__('Topics|See %{totalGroups} %{menuName} in %{groupTitle}').i__({
        totalGroups,
        menuName,
        groupTitle,
      }),
    ];
  }

  return [
    s__('Topics|See all %{menuName}').i__({ menuName }),
    s__('Topics|See all %{menuName} in %{groupTitle}').i__({
      menuName,
      groupTitle,
    }),
  ];
};

const TopicsSuperCategories = () => {
  const { setSuperCategoryView, setTotalAllTopics, setUserAuthorizationInfo } =
    useTopicsIndexContext();
  const displayNavigationDropShadow = useDisplayNavigationDropShadow();
  const secondaryNavigationTheme = useSecondaryNavigationTheme();

  const [superCategories, setSuperCategories] = useState([]);
  const [superCategoriesCount, setSuperCategoriesCount] = useState(0);
  const [isFetchingSuperCategories, setIsFetchingSuperCategories] =
    useState(false);

  const cancelTokenRef = useRef(null);

  const menuName = useTopicsMenuName();

  const title = s__('Topics|Browse all %{menuName}').i__({
    menuName,
  });

  const handleFetchSuperCategories = async () => {
    if (isFetchingSuperCategories && cancelTokenRef.current) {
      cancelTokenRef.current.cancel(CANCEL_FETCH_MESSAGE);
    }
    cancelTokenRef.current = CancelToken.source();

    setIsFetchingSuperCategories(true);

    const response = await fetchSuperCategories({
      cancelToken: cancelTokenRef?.current,
    });

    if (response?.response?.status === HTTP_STATUS_CODE_403) {
      setUserAuthorizationInfo({ isAuthorized: false, error: response });
    } else {
      setUserAuthorizationInfo({ isAuthorized: true });

      if (response?.superCategories) {
        setSuperCategories(response.superCategories);
        setSuperCategoriesCount(response.superCategoriesCount);
        setIsFetchingSuperCategories(false);
      }
    }
  };

  useEffect(() => {
    handleFetchSuperCategories();
  }, []);

  useEffect(() => {
    let totalSuperCategories = {};

    superCategories.forEach((superCategory) => {
      const { id, topics_count: totalTopics } = superCategory;

      totalSuperCategories = {
        ...totalSuperCategories,
        [id]: {
          totalTopics,
        },
      };
    });

    setTotalAllTopics((state) => ({
      ...state,
      [SUPER_CATEGORIES]: {
        isFetchingTopics: isFetchingSuperCategories,
        totalSuperCategories,
      },
    }));
  }, [superCategories, isFetchingSuperCategories]);

  return superCategories?.length > 0 ? (
    <Box
      mt="18px"
      pb="84px"
      display="block"
      data-testid="topics-super-categories"
    >
      <Box
        bg={secondaryNavigationTheme.backgroundColor}
        justifyContent="space-between"
        css={css`
          ${displayNavigationDropShadow && primaryShadow}
        `}
      >
        <ThemeProvider
          theme={secondaryNavigationTheme.themeName}
          fontBrightness={secondaryNavigationTheme.fontBrightness}
        >
          <Container
            pt="27px"
            px={{ _: 5, l: 6 }}
            pb={12}
            flexDirection="column"
          >
            <Grid width="100%" mx={0}>
              <Row>
                <Col>
                  <Box as="h2" font="h2-semibold">
                    {title} ({superCategoriesCount})
                  </Box>
                </Col>
              </Row>
              <Row>
                {superCategories.map((superCategory, index) => {
                  const {
                    description,
                    id,
                    name,
                    picture,
                    topics_count: totalGroups,
                  } = superCategory;

                  const [seeGroupsLabel, seeGroupsA11yLabel] =
                    getSeeGroupsLabels(totalGroups, menuName, name);

                  return (
                    <CustomCol key={index}>
                      <SuperCategoryCard
                        description={description}
                        name={name}
                        picture={picture}
                        seeGroupsAction={() => setSuperCategoryView(id)}
                        seeGroupsLabel={seeGroupsLabel}
                        seeGroupsA11yLabel={seeGroupsA11yLabel}
                      />
                    </CustomCol>
                  );
                })}
              </Row>
            </Grid>
          </Container>
        </ThemeProvider>
      </Box>
    </Box>
  ) : null;
};

export default TopicsSuperCategories;
